import React, { useEffect } from 'react';
import { hotelOrgService } from '../services/hotelOrgService';
import { Hotel } from './useHotels';

export interface User {
  id: string;
  email: string;
  role: string;
}

export interface UseHotelOrgDetailParams {
  organizationId: string;
  limit?: number;
  hotelSearchTerm?: string;
  userSearchTerm?: string;
  hotelSortKey?: string;
  hotelSortOrder?: 'asc' | 'desc' | null;
}
export type HotelOrgTab = 'assigned' | 'unassigned' | 'users';
export interface UseHotelOrgDetailReturn {
  activeTab: HotelOrgTab;
  hotels: Hotel[];
  allHotels: Hotel[];
  users: User[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  metadata: {
    assigned_count: number;
    unassigned_count: number;
    user_count: number;
  };
  setActiveTab: React.Dispatch<React.SetStateAction<HotelOrgTab>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  refreshHotels: () => void;
  refreshUsers: () => void;
}

/**
 * Hook for managing hotel organization details
 * Used on the Hotel Organization Detail page (:id)
 */
export const useHotelOrganizationDetail = (
  params: UseHotelOrgDetailParams
): UseHotelOrgDetailReturn => {
  const {
    organizationId,
    limit = 10,
    hotelSearchTerm = '',
    userSearchTerm = '',
    hotelSortKey = '',
    hotelSortOrder,
  } = params;
  const [activeTab, setActiveTab] = React.useState<HotelOrgTab>('assigned');
  const [hotels, setHotels] = React.useState<Hotel[]>([]);
  const [allHotels, setAllHotels] = React.useState<Hotel[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(0);
  const [hasNextPage, setHasNextPage] = React.useState(false);
  const [hasPrevPage, setHasPrevPage] = React.useState(false);
  const [metadata, setMetadata] = React.useState<any>(null);

  const fetchHotels = React.useCallback(async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const hotelResponse = await hotelOrgService.getHotelsByOrganization(
        organizationId,
        activeTab === 'assigned',
        currentPage,
        limit,
        hotelSearchTerm,
        hotelSortKey,
        hotelSortOrder
      );
      console.log('Fetched hotels for organization:', hotelResponse);

      // Get hotel data
      const hotelData = hotelResponse?.data || [];
      const fetchedHotels = Array.isArray(hotelData) ? hotelData : [];

      // Store all hotels
      setAllHotels(fetchedHotels);
      setMetadata((hotelResponse as any)?.organizationMetadata || null);

      // Use backend pagination data
      const pagination = (hotelResponse as any)?.pagination;
      if (pagination) {
        setHotels(fetchedHotels);
        setTotalCount(pagination.totalCount || 0);
        setTotalPages(pagination.totalPages || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPrevPage(pagination.hasPrevPage || false);
      } else {
        // Fallback if pagination data is not available
        setHotels(fetchedHotels);
        setTotalCount(fetchedHotels.length);
        setTotalPages(1);
        setHasNextPage(false);
        setHasPrevPage(false);
      }
    } catch (error) {
      console.error('Error fetching hotels:', error);
      setHotels([]);
      setTotalCount(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPrevPage(false);
    } finally {
      setLoading(false);
    }
  }, [
    organizationId,
    activeTab,
    currentPage,
    limit,
    hotelSearchTerm,
    hotelSortKey,
    hotelSortOrder,
  ]);

  const fetchUsers = React.useCallback(async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const usersResponse = await hotelOrgService.getUsersByOrganization(
        organizationId
      );
      console.log('Fetched users for organization:', usersResponse);

      // Update metadata from users response
      setMetadata((usersResponse as any)?.organizationMetadata || null);

      // Get users data
      const usersData = usersResponse?.data || [];
      let fetchedUsers = Array.isArray(usersData) ? usersData : [];

      // Filter by user search term if provided
      if (userSearchTerm && userSearchTerm.trim()) {
        const searchLower = userSearchTerm.toLowerCase().trim();
        fetchedUsers = fetchedUsers.filter(
          user =>
            user.email?.toLowerCase().includes(searchLower) ||
            user.role?.toLowerCase().includes(searchLower)
        );
      }

      setUsers(fetchedUsers);
      setTotalCount(fetchedUsers.length);
      setTotalPages(1); // No pagination for users for now
      setHasNextPage(false);
      setHasPrevPage(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setTotalCount(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPrevPage(false);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userSearchTerm]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchHotels();
    }
  }, [fetchHotels, fetchUsers, activeTab]);

  // Reset to page 1 when tab changes or search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, hotelSearchTerm, userSearchTerm]);

  return {
    activeTab,
    hotels,
    allHotels,
    users,
    loading,
    currentPage,
    totalPages,
    totalCount,
    hasNextPage,
    hasPrevPage,
    metadata,
    setActiveTab,
    setCurrentPage,
    refreshHotels: fetchHotels,
    refreshUsers: fetchUsers,
  };
};
